import React, { Fragment, useEffect, useState } from 'react'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { Filter, FilterComponent } from '../../components/_WMS/FilterComponent'
import { filterOptions } from './config/filterOptions'
import { useSnackbar } from '../../hooks/useSnackbar'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { usePagination } from '../../hooks/usePagination'
import { useApi } from '../../hooks/useApi'
import { ApiResponse } from '../../typings/ApiResponse'
import { Warehouse } from '../../interfaces/Warehouse/Warehouse'
import { GenericOrderBy } from '../../components/_EOS/Table'
import { deleteWarehouse, getWarehouseList } from './config/api'
import { GenericTableV2 } from '../../components/_EOS/GenericTable'
import { Status } from '../../hooks/useFetchState'
import { getColumnsConfig } from './config/tableColumns'
import { useModal } from '../../hooks/useModal'
import { Modal } from '../../components/_EOS/Modal'
import { Delete, Edit, Visibility } from '@mui/icons-material'
import { ViewWarehouseModal } from './modal/ViewWarehouseModal'
import { CreateEditWarehouseModal } from './modal/CreateEditWarehouseModal'

interface WarehousePageProps {}

export const WarehousePage: React.FC<WarehousePageProps> = () => {
  const { callApi, state } = useApi<ApiResponse<Warehouse>>()
  const { callApi: requestDeleteWarehouse, state: deleteState } = useApi()
  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([])
  const { isActive, message, openSnackBar, isSuccess } = useSnackbar()
  const pagination = usePagination(5)

  const [orderBy, setOrderBy] = useState<GenericOrderBy<Warehouse>>({
    field: 'warehouseCode' as keyof Warehouse,
    direction: 'asc'
  })

  const columnsConfig = getColumnsConfig()

  const handleSorting = (field: keyof Warehouse, direction: 'asc' | 'desc') => {
    setOrderBy({ field, direction })
  }

  const handlePagination = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestData(selectedFilters, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  const requestData = (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => {
    const filtersToUse = filters !== undefined ? filters : selectedFilters
    const pageToUse = page !== undefined ? page : pagination.page
    const pageSizeToUse =
      pageSize !== undefined ? pageSize : pagination.pageSize

    callApi(
      getWarehouseList(filtersToUse, pageToUse, pageSizeToUse, orderBy)
    ).catch((error) => {
      openSnackBar(error, false)
    })
  }

  const resetAndFetchData = (filters?: Filter[]) => {
    pagination.setPagination(0, pagination.pageSize)
    requestData(filters, 0, pagination.pageSize)
  }

  // Refrescar solo cuando cambia el ordenamiento
  useEffect(() => {
    resetAndFetchData(selectedFilters)
  }, [orderBy])

  const createModal = useModal('Crear Almacén')
  const editModal = useModal<Warehouse>('Editar Almacén')
  const viewModal = useModal<number>('Detalle de Almacén')
  const deleteModal = useModal<Warehouse>('Confirmar Eliminación')

  const handleDelete = (warehouseId: number) => {
    requestDeleteWarehouse(deleteWarehouse(warehouseId))
      .then(() => {
        openSnackBar('Almacén eliminado exitosamente', true)
        deleteModal.closeModal()
        resetAndFetchData()
      })
      .catch((error) => {
        openSnackBar(error.message || 'Error al eliminar', false)
      })
  }

  const handleCreateClick = () => {
    createModal.openModal('md')
  }

  return (
    <Fragment>
      <StandardPage pageTitle=''>
        <FilterComponent
          includeAddButton={true}
          options={filterOptions}
          onSearchClick={resetAndFetchData}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          includeSearchOnClean={true}
          onAddButtonClick={handleCreateClick}
          createButtonTooltip="Crear Almacén"
        />
        <GenericTableV2<Warehouse>
          data={state.data?.items || []}
          columns={columnsConfig}
          keyField='id'
          loading={state.status === Status.Loading}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: state.data?.totalItems || 0,
            onPageChange: handlePagination
          }}
          sorting={{
            field: orderBy.field as keyof Warehouse,
            direction: orderBy.direction,
            onSortChange: handleSorting
          }}
          actions={[
            {
              key: 'edit',
              label: 'Editar',
              onClick: (item) => editModal.openModal('lg', item),
              icon: <Edit />
            },
            {
              key: 'view',
              label: 'Ver Detalle',
              onClick: (row) => viewModal.openModal('md', row.id),
              icon: <Visibility />
            },

            {
              key: 'delete',
              label: 'Eliminar',
              onClick: (item) => deleteModal.openModal('sm', item),
              icon: <Delete />
            }
          ]}
        />
      </StandardPage>

      <Modal
        open={viewModal.isOpen}
        maxWidth={viewModal.size}
        title={viewModal.title}
        onClose={viewModal.closeModal}
        cancelButton={{
          action: viewModal.closeModal,
          title: 'Cerrar'
        }}
      >
        <ViewWarehouseModal
          warehouseId={viewModal?.extendedProps || 0}
          handleSnackbar={openSnackBar}
        />
      </Modal>

      <Modal
        open={createModal.isOpen}
        maxWidth={createModal.size}
        title={createModal.title}
        onClose={createModal.closeModal}
      >
        <CreateEditWarehouseModal
          isEditing={false}
          closeModal={createModal.closeModal}
          onSuccess={requestData}
          handleSnackbar={openSnackBar}
        />
      </Modal>

      <Modal
        open={editModal.isOpen}
        maxWidth={editModal.size}
        title={editModal.title}
        onClose={editModal.closeModal}
      >
        <CreateEditWarehouseModal
          warehouse={editModal.extendedProps}
          isEditing={true}
          closeModal={editModal.closeModal}
          onSuccess={requestData}
          handleSnackbar={openSnackBar}
        />
      </Modal>

      <Modal
        open={deleteModal.isOpen}
        maxWidth={deleteModal.size}
        title={deleteModal.title}
        onClose={deleteModal.closeModal}
        successButton={{
          action: () => handleDelete(deleteModal.extendedProps?.id || 0),
          title: 'Confirmar',
          disabled: deleteState.status === Status.Loading
        }}
        cancelButton={{
          action: () => deleteModal.closeModal(),
          title: 'Cancelar',
          disabled: deleteState.status === Status.Loading
        }}
      >
        <span>
          ¿Desea eliminar el Almacén:{' '}
          <strong>{deleteModal.extendedProps?.warehouseName}</strong>?
        </span>
      </Modal>

      <SnackBar
        message={message}
        status={isSuccess ? 'success' : 'error'}
        open={isActive}
      />
    </Fragment>
  )
}
